import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ReceiptLong as RegisterIcon,
  PlayArrow as RunIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import itemCostLayerService from '../../../../services/itemCostLayerService';
import { hasMixedBasis } from '../../../../utils/costBasisDisplay';
import { formatSourceRef } from '../../../../utils/sourceRef';
import inventoryReportService from '../../../../services/inventoryReportService';
import type {
  TransactionRegisterParams,
  TransactionRegisterResponse,
  TransactionRegisterRow,
} from '../../../../services/inventoryReportService';
import venueLocationService from '../../../../services/venueLocationService';
import inventoryCategoryService from '../../../../services/inventoryCategoryService';
import type { VenueLocation, InventoryCategory, InventorySubcategory } from '../../../../types/venue';
import DateRangeFilter from '../../../../components/venue/DateRangeFilter';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
import InventoryItemSearchBox from '../../../../components/venue/InventoryItemSearchBox';
import ColumnVisibilityMenu from '../../../../components/venue/ColumnVisibilityMenu';
import TransactionRegisterHelpModal from '../../../../components/venue/TransactionRegisterHelpModal';
import PageHeader from '../../../../components/venue/PageHeader';
import FilterBar from '../../../../components/venue/FilterBar';
import EmptyState from '../../../../components/venue/EmptyState';
import {
  downloadTransactionRegisterXlsx,
  downloadTransactionRegisterCsv,
} from '../../../../utils/inventoryTransactionRegisterTemplate';
import { formatQty } from '../../../../utils/quantityFormat';
import { fromMinorUnitsOrNull } from '../../../../utils/money';
import { costBasisCell, type CostCell } from '../../../../utils/costBasisDisplay';
import { sizeCaption, humanizeItemType, ITEM_TYPE_FILTER_OPTIONS } from '../../../../utils/inventoryItemDisplay';
import { useColumnVisibility } from '../../../../utils/columnVisibility';
import type { ColumnDef } from '../../../../utils/columnVisibility';

// Money display: minor units → major, fixed 2dp. Blank for null (no invented
// zeros). Conversion happens ONLY here at the display boundary (fromMinorUnits).
const fmtMoney = (minor: number | null | undefined): string => {
  const major = fromMinorUnitsOrNull(minor);
  return major == null ? '—' : major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// COSTING.1d — render a cost cell per its recorded/estimated/uncosted basis.
const renderCostCell = (cell: CostCell): React.ReactNode => {
  if (cell.kind === 'uncosted' || cell.cost == null) {
    return cell.tooltip
      ? <Tooltip title={cell.tooltip}><Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help', color: 'text.secondary' }}>—</Box></Tooltip>
      : <span>—</span>;
  }
  if (cell.kind === 'recorded') return <span>{fmtMoney(cell.cost)}</span>;
  // estimated → text.secondary + dotted-tooltip idiom
  return (
    <Tooltip title={cell.tooltip ?? 'Estimated'}>
      <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help', color: 'text.secondary' }}>{fmtMoney(cell.cost)}</Box>
    </Tooltip>
  );
};

const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
const dayStart = (day: string): string => `${day}T00:00:00.000Z`;
const dayEnd = (day: string): string => `${day}T23:59:59.999Z`;

// Filterable movement types. DEPLETE and RETURN_TO_SUPPLIER are intentionally
// omitted: they are dead enum values with no writers (INVENTORY.ASSESS audit),
// so offering them as filters is noise. They remain renderable in the table via
// MovementTypeBadge should any historical row ever carry them.
const MOVEMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'RECEIVE', label: 'Receive' },
  { value: 'OPENING_BALANCE', label: 'Opening Stock' },
  { value: 'TRANSFER_IN', label: 'Transfer In' },
  { value: 'TRANSFER_OUT', label: 'Transfer Out' },
  { value: 'SALE', label: 'Sale' },
  { value: 'SALE_REVERSAL', label: 'Sale Reversal' },
  { value: 'COUNT_VARIANCE', label: 'Count Variance' },
  { value: 'MANUAL_ADJUSTMENT', label: 'Adjustment' },
  { value: 'WASTE', label: 'Waste' },
  { value: 'PRODUCTION_CONSUMPTION', label: 'Production Consumption' },
  { value: 'PRODUCTION_OUTPUT', label: 'Production Output' },
];

// RECIPE-TYPE.R7 — one shared filter list (see inventoryItemDisplay).
const ITEM_TYPE_OPTIONS = ITEM_TYPE_FILTER_OPTIONS;

// ── Table columns ─────────────────────────────────────────────────────────────
// Fixed widths (table-layout: fixed) so item names can't bleed under later
// columns. The width-less Item column absorbs the remaining space; everything
// else is a hard width. Order: Posted, Item, Type, Qty In, Qty Out,
// [Balance — stock-card only], Source, By/Notes, Location (last).
//
// REPORTING-CENTER.2.2 width tuning (Andrii's prod review): Posted renders
// date + time on two lines so it stays narrow and never touches Item; Type is
// sized to its chip; Qty In/Out are compact (1–3 digit numbers) and adjacent;
// Source is narrower. Every trim flows into Item, which absorbs the remainder.
type ColMeta = ColumnDef & { width?: number; align?: 'right'; stockCardOnly?: boolean; headerHint?: string };

const REGISTER_COLUMNS: ColMeta[] = [
  { key: 'posted',    label: 'Posted',      locked: true, width: 124 },
  { key: 'item',      label: 'Item',        locked: true },
  { key: 'type',      label: 'Type',        width: 124 },
  { key: 'qtyIn',     label: 'Qty In',      width: 76,  align: 'right' },
  { key: 'qtyOut',    label: 'Qty Out',     width: 76,  align: 'right' },
  { key: 'balance',   label: 'Balance',     width: 96,  align: 'right', stockCardOnly: true },
  // Cost columns. Visible by default (they're the point); individually hideable
  // via the Columns menu. REGISTER-REVENUE.1 — Sale Price and Est. Revenue are
  // gone; see TransactionRegisterRow for why a component ledger cannot carry
  // either honestly.
  { key: 'unitCost',  label: 'Unit Cost',   width: 96,  align: 'right' },
  { key: 'value',     label: 'Value',       width: 100, align: 'right' },
  { key: 'source',    label: 'Source',      width: 136 },
  { key: 'byNotes',   label: 'By / Notes',  width: 200 },
  // REGISTER-VENUE.1 — who made the movement. On a shared cellar every venue's
  // sales sit side by side, and this is the only thing telling them apart.
  { key: 'venue',     label: 'Venue',       width: 140 },
  { key: 'location',  label: 'Location',    width: 140 },
];

// Source-reference formatting lives in utils/sourceRef — the item's movement
// history and the movement feed show the same thing and were showing it
// differently.

// Columns offered in the show/hide menu (Balance is mode-driven, not a toggle).
const REGISTER_MENU_COLUMNS: ColumnDef[] = REGISTER_COLUMNS
  .filter(c => !c.stockCardOnly)
  .map(({ key, label, locked }) => ({ key, label, locked }));

// Posted renders on two lines: date on top, time as a caption beneath. Keeps
// the column narrow (never touching Item) while preserving the full timestamp.
const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};
const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  });
};

const TransactionRegister: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();
  const { canViewStockCosts } = useAuth();
  const showCost = canViewStockCosts();
  const [activatedAt, setActivatedAt] = useState<string | null>(null);
  const base = `/admin/venues/${venueId}`;

  // ── Filter inputs ──────────────────────────────────────────────────────────
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [fromDay, setFromDay] = useState(isoDay(monthAgo));
  const [toDay, setToDay] = useState(isoDay(today));
  const [locationId, setLocationId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [itemType, setItemType] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [item, setItem] = useState<{ _id: string; displayName?: string } | null>(null);
  const [actingVenueId, setActingVenueId] = useState('');

  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [subcategories, setSubcategories] = useState<InventorySubcategory[]>([]);

  // ── Result state ───────────────────────────────────────────────────────────
  const [data, setData] = useState<TransactionRegisterResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [page, setPage] = useState(0); // 0-indexed for TablePagination
  const [pageSize, setPageSize] = useState(50);

  // The filter snapshot backing the current result set, so paging doesn't pick
  // up half-edited filter inputs.
  const [applied, setApplied] = useState<TransactionRegisterParams | null>(null);

  useEffect(() => {
    if (!venueId) return;
    // REGISTER-VENUE.1 — the same set the register reads: every location this
    // venue sells from, retired ones included because their history is still in
    // "All locations". A location offered here that the register does not read
    // would always come back empty.
    venueLocationService.getLocations(venueId, 'SELL', { includeInactive: true })
      .then(locs => setLocations([...locs].sort((a, b) => Number(b.active) - Number(a.active))))
      .catch(() => { /* non-blocking */ });
    inventoryCategoryService.getCategories(venueId, {}).then(setCategories).catch(() => { /* non-blocking */ });
    // COSTING.1d — venue activation date for the once-only mixed-basis caption.
    if (showCost) itemCostLayerService.getCostingStatus(venueId).then(s => setActivatedAt(s.activatedAt)).catch(() => { /* non-blocking */ });
  }, [venueId, showCost]);

  // ── Column visibility (persisted per venue) ─────────────────────────────────
  // Location defaults HIDDEN for single-location venues (the column is dead
  // weight there) and visible once a venue has >1 active location. A user's
  // explicit show/hide choice overrides this default and is remembered.
  const activeLocationCount = locations.filter(l => l.active).length;
  // Venue matters once the venue reaches stock it does not own; on its own
  // shelves every row would name itself.
  const reachesSharedStock = locations.some(l => l.isOwn === false);
  const columnDefaults = useMemo<Record<string, boolean>>(() => ({
    type: true, qtyIn: true, qtyOut: true, source: true, byNotes: true,
    // Money columns ship visible by default (REPORTING-CENTER.2.3).
    unitCost: true, value: true,
    location: activeLocationCount > 1,
    venue: reachesSharedStock,
  }), [activeLocationCount, reachesSharedStock]);
  const { visible, toggle, reset } = useColumnVisibility(
    'txRegister.cols', venueId, REGISTER_MENU_COLUMNS, columnDefaults,
  );

  useEffect(() => {
    setSubcategoryId('');
    if (!venueId || !categoryId) { setSubcategories([]); return; }
    inventoryCategoryService.getSubcategories(venueId, categoryId, {}).then(setSubcategories).catch(() => setSubcategories([]));
  }, [venueId, categoryId]);

  const buildParams = useCallback((): TransactionRegisterParams => ({
    from: dayStart(fromDay),
    to: dayEnd(toDay),
    locationId: locationId || undefined,
    categoryId: categoryId || undefined,
    subcategoryId: subcategoryId || undefined,
    itemType: itemType || undefined,
    types: types.length ? types : undefined,
    itemId: item?._id || undefined,
    actingVenueId: actingVenueId || undefined,
  }), [fromDay, toDay, locationId, categoryId, subcategoryId, itemType, types, item, actingVenueId]);

  const fetchPage = useCallback(async (params: TransactionRegisterParams, pageIdx: number, size: number) => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await inventoryReportService.getTransactionRegister(venueId, {
        ...params, page: pageIdx + 1, pageSize: size,
      });
      setData(res);
    } catch (err: any) {
      setData(null);
      setError(err?.response?.data?.error?.message ?? 'Failed to load the transaction register. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  const run = useCallback(() => {
    const params = buildParams();
    setApplied(params);
    setPage(0);
    fetchPage(params, 0, pageSize);
  }, [buildParams, fetchPage, pageSize]);

  // Initial load with defaults.
  useEffect(() => {
    if (!venueId) return;
    const params = buildParams();
    setApplied(params);
    fetchPage(params, 0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  const handleChangePage = (_: unknown, newPage: number) => {
    if (!applied) return;
    setPage(newPage);
    fetchPage(applied, newPage, pageSize);
  };
  const handleChangePageSize = (e: React.ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value, 10);
    setPageSize(size);
    setPage(0);
    if (applied) fetchPage(applied, 0, size);
  };

  const runExport = useCallback(async (kind: 'xlsx' | 'csv') => {
    if (!venueId || !applied) return;
    setExporting(true);
    setError(null);
    try {
      const full = await inventoryReportService.getTransactionRegister(venueId, { ...applied, export: true });
      if (kind === 'xlsx') downloadTransactionRegisterXlsx(full, activatedAt);
      else downloadTransactionRegisterCsv(full, activatedAt);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [venueId, applied]);

  const setStockCardItem = (row: TransactionRegisterRow) => {
    setItem({ _id: row.inventoryItemId, displayName: row.itemName ?? undefined });
    const params = { ...buildParams(), itemId: row.inventoryItemId };
    setApplied(params);
    setPage(0);
    fetchPage(params, 0, pageSize);
  };

  const isStockCard = data?.meta.mode === 'STOCK_CARD';
  const stockCard = data?.meta.stockCard;

  // Columns actually rendered this pass: locked always; Balance only in stock-
  // card mode; the rest per the visibility map. Drives colgroup, header, body,
  // and the balance-row colSpans so they can never drift apart.
  // COSTING.1d — hide cost columns entirely for users without stock-cost
  // visibility (the api strips them to null regardless; this avoids "—" columns).
  const COST_COLS = new Set(['unitCost', 'value']);
  const activeColumns = useMemo<ColMeta[]>(() => REGISTER_COLUMNS.filter(c => {
    if (!showCost && COST_COLS.has(c.key)) return false;
    if (c.stockCardOnly) return isStockCard;
    if (c.locked) return true;
    return visible[c.key];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [visible, isStockCard, showCost]);

  // A min width so the flexible Item column never collapses; below it the
  // container scrolls horizontally instead of letting columns collide.
  const tableMinWidth = activeColumns.reduce((sum, c) => sum + (c.width ?? 240), 0);
  const balanceIndex = activeColumns.findIndex(c => c.key === 'balance');

  const sourceLink = (type: string | null, id: string | null): string | null => {
    if (!type || !id) return null;
    if (type === 'STOCK_COUNT') return `${base}/stock-counts/${id}`;
    if (type === 'PURCHASE_ORDER') return `${base}/purchasing/${id}`;
    if (type === 'TRANSFER') return `${base}/transfers/${id}`;
    return null;
  };

  const rows = data?.rows ?? [];

  // The picked venue stays offered even if the last result no longer names it,
  // so the select never holds a value it cannot show.
  const venueOptions = useMemo(() => {
    const offered = data?.meta.actingVenues ?? [];
    if (!actingVenueId || offered.some(v => v.venueId === actingVenueId)) return offered;
    return [...offered, { venueId: actingVenueId, venueName: data?.meta.actingVenueName ?? null }];
  }, [data, actingVenueId]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<RegisterIcon />}
        title="Transaction Register"
        onHelp={() => setHelpOpen(true)}
        helpTitle="What the register and stock card show"
        subtitle="Every inventory movement — received, transferred, sold, adjusted, wasted — with In/Out quantities. Pick a single item for its Stock Card with a running balance."
      />

      {/* ── Filter bar ──────────────────────────────────────────────────────── */}
      <FilterBar
        actionsLeft={<ColumnVisibilityMenu columns={REGISTER_MENU_COLUMNS} visible={visible} onToggle={toggle} onReset={reset} disabled={loading} />}
        actions={
          <>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RunIcon />} onClick={run} disabled={loading || !fromDay || !toDay}>
              Run
            </Button>
            <Tooltip title="Export the full filtered set as XLSX">
              <span>
                <Button variant="outlined" startIcon={<ExcelIcon />} onClick={() => runExport('xlsx')} disabled={!applied || loading || exporting || rows.length === 0}>XLSX</Button>
              </span>
            </Tooltip>
            <Tooltip title="Export the full filtered set as CSV">
              <span>
                <Button variant="outlined" startIcon={<CsvIcon />} onClick={() => runExport('csv')} disabled={!applied || loading || exporting || rows.length === 0}>CSV</Button>
              </span>
            </Tooltip>
          </>
        }
      >
        <DateRangeFilter from={fromDay} to={toDay} onFromChange={setFromDay} onToChange={setToDay} disabled={loading} />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Location</InputLabel>
          <Select value={locationId} label="Location" onChange={e => setLocationId(e.target.value)} disabled={loading}>
            <MenuItem value="">All locations</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>
                {loc.displayName}{loc.active ? '' : ' (inactive)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* REGISTER-VENUE.1 — whose movements. Offered from the venues that made
            something in the last result, so every choice has rows behind it. */}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Venue</InputLabel>
          <Select value={actingVenueId} label="Venue" onChange={e => setActingVenueId(e.target.value)} disabled={loading}>
            <MenuItem value="">All venues</MenuItem>
            {venueOptions.map(v => (
              <MenuItem key={v.venueId} value={v.venueId}>
                {v.venueName ?? `Venue ${v.venueId.slice(-6)}`}{v.venueId === venueId ? ' (this venue)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Movement types</InputLabel>
          <Select
            multiple
            value={types}
            onChange={e => setTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label="Movement types" />}
            renderValue={(sel) => (sel as string[]).length === MOVEMENT_TYPE_OPTIONS.length || (sel as string[]).length === 0 ? 'All' : `${(sel as string[]).length} selected`}
            disabled={loading}
          >
            {MOVEMENT_TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Item type</InputLabel>
          <Select value={itemType} label="Item type" onChange={e => setItemType(e.target.value)} disabled={loading}>
            <MenuItem value="">All</MenuItem>
            {/* Label only — the option list itself is unchanged in R6. */}
            {ITEM_TYPE_OPTIONS.map(t => <MenuItem key={t} value={t}>{humanizeItemType(t)}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryId} label="Category" onChange={e => setCategoryId(e.target.value)} disabled={loading}>
            <MenuItem value="">All categories</MenuItem>
            {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }} disabled={!categoryId || loading}>
          <InputLabel>Subcategory</InputLabel>
          <Select value={subcategoryId} label="Subcategory" onChange={e => setSubcategoryId(e.target.value)}>
            <MenuItem value="">All subcategories</MenuItem>
            {subcategories.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Stock Card picker — the primary control, so it takes the leftover
            width on the row (and drops to full width when the row wraps). */}
        <Box sx={{ flexGrow: 1, flexBasis: 280, minWidth: 260, maxWidth: 440 }}>
          <InventoryItemSearchBox
            venueId={venueId}
            value={item as any}
            onChange={(it) => setItem(it ? { _id: it._id, displayName: (it as any).displayName } : null)}
            label="Stock Card — item"
            placeholder="Search an item…"
          />
        </Box>
      </FilterBar>

      {/* ── Stock card banner ───────────────────────────────────────────────── */}
      {isStockCard && stockCard && (
        <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: theme.palette.action.hover }}>
          <Chip
            label={`Stock Card: ${stockCard.itemName ?? 'item'}`}
            onDelete={() => { setItem(null); const p = { ...buildParams(), itemId: undefined }; setApplied(p); setPage(0); fetchPage(p, 0, pageSize); }}
            deleteIcon={<CloseIcon />}
            color="primary"
          />
          <Typography variant="body2" color="text.secondary">
            Opening <strong>{formatQty(stockCard.openingBalance)}</strong> · Closing <strong>{formatQty(stockCard.closingBalance)}</strong>
            {(stockCard.openingValue != null || stockCard.closingValue != null) && (
              <> · Opening Value <strong>{fmtMoney(stockCard.openingValue)}</strong> · Closing Value <strong>{fmtMoney(stockCard.closingValue)}</strong></>
            )}
          </Typography>
        </Paper>
      )}

      {/* ── Truncation warning ──────────────────────────────────────────────── */}
      {data?.meta.truncated && (
        <Alert severity="warning">
          {isStockCard ? (
            <>
              This item has {data.meta.total.toLocaleString()} movements in the range — the{' '}
              <strong>movement list is truncated</strong>, but the <strong>Opening and Closing
              balances are exact</strong> (computed from the full ledger). Narrow the date range for
              the complete line-by-line list.
            </>
          ) : (
            <>
              This selection has {data.meta.total.toLocaleString()} movements — exports are capped, so
              the downloaded file is truncated. Narrow the date range or filters for the complete set.
            </>
          )}
        </Alert>
      )}

      {/* ── States ──────────────────────────────────────────────────────────── */}
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

      {!loading && !error && data && rows.length === 0 && (
        <EmptyState
          icon={RegisterIcon}
          message="No movements match these filters. Adjust the date range or filters and run again."
        />
      )}

      {/* COSTING.1d — once-only mixed-basis caption (keyed to activation date). */}
      {!loading && data && rows.length > 0 && showCost && activatedAt && hasMixedBasis(rows) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
          Values before {new Date(activatedAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })} are estimated at current cost.
        </Typography>
      )}

      {!loading && data && rows.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
              <colgroup>
                {activeColumns.map(c => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
              </colgroup>
              <TableHead>
                <TableRow>
                  {activeColumns.map(c => (
                    <TableCell key={c.key} align={c.align}>
                      {c.headerHint ? (
                        <Tooltip title={c.headerHint}>
                          <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>{c.label}</Box>
                        </Tooltip>
                      ) : c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Stock card: opening balance line — label spans up to Balance;
                    Balance shows the qty and the Value column shows Opening Value. */}
                {isStockCard && stockCard && page === 0 && balanceIndex >= 0 && (
                  <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                    <TableCell colSpan={balanceIndex} sx={{ fontWeight: 700 }}>Opening Balance</TableCell>
                    {activeColumns.slice(balanceIndex).map(col => {
                      if (col.key === 'balance') return <TableCell key="balance" align="right" sx={{ fontWeight: 700 }}>{formatQty(stockCard.openingBalance)}</TableCell>;
                      if (col.key === 'value') return <TableCell key="value" align="right" sx={{ fontWeight: 700 }}>{fmtMoney(stockCard.openingValue)}</TableCell>;
                      return <TableCell key={col.key} />;
                    })}
                  </TableRow>
                )}

                {rows.map(row => {
                  const link = sourceLink(row.sourceDocumentType, row.sourceDocumentId);
                  const size = sizeCaption(row.itemName, row.sizeValue, row.sizeUnit);
                  const renderCell = (col: ColMeta): React.ReactNode => {
                    switch (col.key) {
                      case 'posted':
                        return (
                          <TableCell key="posted" sx={{ whiteSpace: 'nowrap', pr: 2 }}>
                            <Typography variant="body2" lineHeight={1.3}>{fmtDate(row.date)}</Typography>
                            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>{fmtTime(row.date)}</Typography>
                          </TableCell>
                        );
                      case 'item':
                        return (
                          <TableCell key="item">
                            <MuiLink
                              component="button"
                              type="button"
                              onClick={() => setStockCardItem(row)}
                              sx={{ textAlign: 'left', display: 'block', maxWidth: '100%', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                              title={`Open stock card for ${row.itemName ?? ''}`}
                            >
                              <Typography variant="body2" fontWeight={600} noWrap title={row.itemName ?? undefined}>
                                {row.itemName ?? `(item ${row.inventoryItemId.slice(-6)})`}
                              </Typography>
                            </MuiLink>
                            {size && <Typography variant="caption" color="text.secondary" noWrap display="block">{size}</Typography>}
                          </TableCell>
                        );
                      case 'type':
                        return <TableCell key="type" sx={{ overflow: 'hidden' }}><MovementTypeBadge type={row.movementType as any} sx={{ maxWidth: '100%' }} /></TableCell>;
                      case 'qtyIn':
                        return <TableCell key="qtyIn" align="right" sx={{ whiteSpace: 'nowrap', color: row.qtyIn ? '#1a7f37' : theme.palette.text.disabled, fontWeight: row.qtyIn ? 600 : 400 }}>{row.qtyIn ? formatQty(row.qtyIn) : '—'}</TableCell>;
                      case 'qtyOut':
                        return <TableCell key="qtyOut" align="right" sx={{ whiteSpace: 'nowrap', color: row.qtyOut ? '#cf222e' : theme.palette.text.disabled, fontWeight: row.qtyOut ? 600 : 400 }}>{row.qtyOut ? formatQty(row.qtyOut) : '—'}</TableCell>;
                      case 'balance':
                        return <TableCell key="balance" align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{row.runningBalance != null ? formatQty(row.runningBalance) : '—'}</TableCell>;
                      case 'unitCost':
                        // COSTING.1d — recorded cost where stamped, CURRENT_LAST_COST
                        // as a labeled estimate fallback; keyed off costBasis.
                        return (
                          <TableCell key="unitCost" align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {renderCostCell(costBasisCell(row.costBasis, row.recordedUnitCost, row.unitCost))}
                          </TableCell>
                        );
                      case 'value': {
                        const vCell = costBasisCell(row.costBasis, row.recordedTotalCost, row.value);
                        const v = vCell.cost;
                        return (
                          <TableCell key="value" align="right" sx={{ whiteSpace: 'nowrap', color: v ? (v > 0 ? '#1a7f37' : '#cf222e') : theme.palette.text.disabled }}>
                            {renderCostCell(vCell)}
                          </TableCell>
                        );
                      }
                      case 'source': {
                        const ref = formatSourceRef(row.sourceDocumentId, row.sourceDocumentNumber);
                        return (
                          <TableCell key="source" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.sourceDocumentType ? (
                              <>
                                {link ? (
                                  <MuiLink component={RouterLink} to={link} sx={{ fontSize: '0.8rem', display: 'block' }} noWrap>{row.sourceDocumentType.replace(/_/g, ' ')}</MuiLink>
                                ) : (
                                  <Typography variant="caption" color="text.secondary" noWrap display="block">{row.sourceDocumentType.replace(/_/g, ' ')}</Typography>
                                )}
                                {ref && (
                                  <Tooltip title={ref.full}>
                                    <Typography
                                      variant="caption"
                                      color={row.sourceDocumentNumber ? 'text.secondary' : 'text.disabled'}
                                      noWrap
                                      display="block"
                                      /* An order number is prose and reads as prose; a raw
                                         ObjectId is a machine value and keeps the monospace
                                         face that makes it comparable character by character. */
                                      sx={row.sourceDocumentNumber
                                        ? { fontSize: '0.72rem' }
                                        : { fontFamily: 'monospace', fontSize: '0.68rem' }}
                                    >{ref.text}</Typography>
                                  </Tooltip>
                                )}
                              </>
                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                        );
                      }
                      case 'byNotes':
                        return (
                          <TableCell key="byNotes">
                            <Typography variant="caption" color="text.secondary" noWrap display="block">{row.postedBy ?? '—'}</Typography>
                            {row.notes && (
                              <Tooltip title={row.notes}>
                                <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block' }}>{row.notes}</Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                        );
                      case 'venue':
                        return <TableCell key="venue" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><Typography variant="body2" color="text.secondary" noWrap>{row.actingVenueName ?? (row.actingVenueId ? `Venue ${row.actingVenueId.slice(-6)}` : '—')}</Typography></TableCell>;
                      case 'location':
                        return <TableCell key="location" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><Typography variant="body2" color="text.secondary" noWrap>{row.locationName ?? '—'}</Typography></TableCell>;
                      default:
                        return <TableCell key={col.key} />;
                    }
                  };
                  return <TableRow key={row.movementId} hover>{activeColumns.map(renderCell)}</TableRow>;
                })}

                {/* Stock card: closing balance line on the last page — Balance qty
                    + Closing Value in the Value column. */}
                {isStockCard && stockCard && data?.pagination && page === data.pagination.totalPages - 1 && balanceIndex >= 0 && (
                  <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                    <TableCell colSpan={balanceIndex} sx={{ fontWeight: 700 }}>Closing Balance</TableCell>
                    {activeColumns.slice(balanceIndex).map(col => {
                      if (col.key === 'balance') return <TableCell key="balance" align="right" sx={{ fontWeight: 700 }}>{formatQty(stockCard.closingBalance)}</TableCell>;
                      if (col.key === 'value') return <TableCell key="value" align="right" sx={{ fontWeight: 700 }}>{fmtMoney(stockCard.closingValue)}</TableCell>;
                      return <TableCell key={col.key} />;
                    })}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data?.pagination && (
            <TablePagination
              component="div"
              count={data.pagination.totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangePageSize}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          )}
        </Paper>
      )}

      <TransactionRegisterHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default TransactionRegister;
